import WarningIcon from '@mui/icons-material/Warning';
import { Alert, SxProps } from '@mui/material';
import { type ReactNode } from 'react';
import { useIntl } from 'react-intl';
import { useParams } from 'react-router-dom';

import { useFeatureFlagSplit } from '@verantos-dev/app-state-base';
import {
  selectHasStagedChanges,
  selectLatestStatus,
  useCohortDefinitionStore,
} from '@verantos-dev/feature-analytical-variables';
import { CohortAttritionTable } from '@verantos-dev/feature-cohort-attrition';
import { CohortCharacteristics } from '@verantos-dev/feature-cohort-characteristics';
import { CohortSubjectsList } from '@verantos-dev/feature-subjects-list';
import { WorkflowMessage } from '@verantos-dev/shared-components';
import { CohortDefinitionStatus } from '@verantos-dev/types-cohorts';

import { resultsPageTabMessagesEn } from '../../../../locale/cohort-section-messages/resultsPageTabMessagesEn';
import { PageTabContent } from '../../cohort-builder-page-layout/StyledPageTabContent/PageTabContent';

interface ResultsPageTabContentProps {
  /**
   * Layout override forwarded to the content wrapper. The gen-3 Results tab
   * passes a left-aligned column layout (M4 visual audit); the legacy page
   * omits it and keeps the centered wrap layout.
   */
  contentSx?: SxProps;
  /**
   * Renderer for the workflow-state empty content (draft / processing /
   * error). The gen-3 Results tab passes one that renders the same outlined
   * card + centered empty state as its SQL tab; the legacy page omits it and
   * falls back to the default elevated {@link WorkflowMessage}.
   */
  renderEmptyState?: (args: { id: string; title: string; message: string }) => ReactNode;
}

// TODO: add test cases for all the internal components, hooks, and helpers
export const ResultsPageTabContent = ({
  contentSx,
  renderEmptyState,
}: ResultsPageTabContentProps) => {
  const intl = useIntl();
  const { projectId, cohortId } = useParams();
  const zustandStatus = useCohortDefinitionStore(selectLatestStatus);
  const latestCohortStatus = zustandStatus;
  // TODO: cleanup with explicit_save ff
  const isExplicitSave = useFeatureFlagSplit('explicit_save');
  const hasStagedChanges = useCohortDefinitionStore(selectHasStagedChanges);

  if (!projectId || !cohortId) {
    return null;
  }

  // if the cohort is in a workflow state, show the workflow states
  if (latestCohortStatus && latestCohortStatus !== CohortDefinitionStatus.PROCESSED) {
    return (
      <ResultsWorkflowStates
        latestCohortStatus={latestCohortStatus}
        renderEmptyState={renderEmptyState}
      />
    );
  }

  // if the cohort is processed, show the results
  if (latestCohortStatus === CohortDefinitionStatus.PROCESSED) {
    return (
      <>
        {!!isExplicitSave && hasStagedChanges && (
          <Alert
            severity="warning"
            icon={<WarningIcon fontSize="inherit" />}
            sx={{ py: 0.5, borderRadius: 0 }}
          >
            {intl.formatMessage({ id: 'projects.cohortBuilderPage.results.stagedChangesWarning' })}
          </Alert>
        )}
        <PageTabContent sx={contentSx}>
          <CohortAttritionTable projectId={projectId} cohortId={cohortId} />
          <CohortCharacteristics projectId={projectId} cohortId={cohortId} />
          <CohortSubjectsList projectId={projectId} cohortId={cohortId} />
        </PageTabContent>
      </>
    );
  }

  // if the cohort is not in a workflow state or processed, return null
  return null;
};

const workflowMessageSx: SxProps = { width: '100%', height: '348px' };

const ResultsWorkflowStates = ({
  latestCohortStatus,
  renderEmptyState,
}: {
  latestCohortStatus: CohortDefinitionStatus;
  renderEmptyState?: (args: { id: string; title: string; message: string }) => ReactNode;
}) => {
  const state = (() => {
    if (
      latestCohortStatus === CohortDefinitionStatus.DRAFT ||
      latestCohortStatus === CohortDefinitionStatus.UNSAVED
    ) {
      return {
        id: 'results-draft-state-workflowMessage',
        title: resultsPageTabMessagesEn.draftTitle,
        message: resultsPageTabMessagesEn.draftMessage,
      };
    }
    if (latestCohortStatus === CohortDefinitionStatus.PROCESSING) {
      return {
        id: 'results-processing-state-workflowMessage',
        title: resultsPageTabMessagesEn.processingTitle,
        message: resultsPageTabMessagesEn.processingMessage,
      };
    }
    // fall through: cohort is in an error / unexpected state
    return {
      id: 'results-processing-state-workflowMessage',
      title: resultsPageTabMessagesEn.errorTitle,
      message: resultsPageTabMessagesEn.errorMessage,
    };
  })();

  // Gen-3 renders its own outlined-card empty state (parity with the SQL tab);
  // the legacy page falls back to the default elevated WorkflowMessage.
  if (renderEmptyState) {
    return <>{renderEmptyState(state)}</>;
  }

  return (
    <WorkflowMessage
      id={state.id}
      title={state.title}
      message={state.message}
      sx={workflowMessageSx}
    />
  );
};
